<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.domains.title')"
    :sub-title="$t('resources.domains.sub_title')">
    <template #actions>
      <v-btn
        v-if="$can('view', 'purchase domain')"
        color="primary"
        outlined
        class="spacing-right--16"
        @click="openDomainPurchase">
        {{ $t("resources.domains.purchase") }}
      </v-btn>
      <v-btn
        v-if="totalItems || noData || loading"
        @click="connectDomainHandler"
        color="primary"
        class="left-icon"
        :loading="connectDomainLoading"
        ><plus-icon class="mr-2"></plus-icon>Connect
      </v-btn>
    </template>
    <template #default>
      <DomainConnectModal v-model="displayConnectDomainModal" />
      <PaywallLimitModal
        v-model="displayPaywallLimitModal"
        :title="$t('resources.domains.paywall_limit_modal.title')"
        :cta-text="$t('settings.paywall_limit_modal.cta_text')"
        :subtitle="$t('resources.domains.paywall_limit_modal.subtitle')"
        :editor-subtitle="$t('resources.domains.paywall_limit_modal.editor_subtitle')"
        :agency-subtitle="$t('resources.domains.paywall_limit_modal.agency_subtitle')"
        @confirm="
          () => {
            $router.push({ name: 'settings', query: { tab: 'billing_payments' } });
            displayPaywallLimitModal = false;
          }
        "></PaywallLimitModal>
      <va-list
        ref="list"
        :filters="filters"
        :filter.sync="filter"
        :labels-list="labelsList"
        disable-export
        disable-create
        dynamic-columns-enabled
        :columns="fields"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        @update:columns="updateColumns">
        <template #no-data>
          <empty-list
            :title="$t('resources.domains.list_page_empty_list_title')"
            :subtitle="$t('resources.domains.list_page_empty_list_subtitle')">
            <template #create>
              <div class="empty-list__create">
                <v-btn color="primary" class="left-icon" @click="connectDomainHandler">
                  <plus-icon class="mr-2"></plus-icon>
                  {{ $t("custom.empty_list.create") }}
                </v-btn>
              </div>
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template #[`filter.q`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            :label="''"
            :placeholder="$t('generic.search_label')"
            @input="val => update('q', val)"></v-text-field>
        </template>
        <va-data-table
          disable-select
          :disable-clone="true"
          :fields="activeColumns"
          class="data-table-custom-class"
          @row-click="handleRowClick">
          <template #[`field.url`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <div class="ellipsis-wrapper">
                <span class="ellipsis-content">{{ item.url }}</span>
                <v-tooltip v-if="item.default" top>
                  <template #activator="{ on }">
                    <div v-on="on" class="default__badge">{{ $t("resources.domains.default_domain") }}</div>
                  </template>
                  <p class="tooltip-content">{{ $t("resources.domains.default_domain_disclaimer") }}</p>
                </v-tooltip>
              </div>
            </div>
          </template>
          <template #[`field.name`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #[`field.status`]="{ item }">
            <v-chip :color="getDomainStatusInfo(item).chipColor">{{ getDomainStatusInfo(item).label }}</v-chip>
          </template>
          <template #[`field.alternativeContentParameter`]="{ item }">
            <div v-if="item.alternativeContentParameter" class="d-flex align-center gap-1">
              <div>{{ `${DOMAIN_PARAMETER_QUERY_KEY}=${item.alternativeContentParameter}` }}</div>
              <CopyText
                :value="`${DOMAIN_PARAMETER_QUERY_KEY}=${item.alternativeContentParameter}`"
                display-icon
                :display-text="false"></CopyText>
            </div>
          </template>
          <template #[`field.label.name`]="{ item }">
            <v-tooltip top v-if="item.additionalLabels.length > 0 && item.label">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.label, $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.label, $vuetify.theme.dark) }">
                    {{ item.label.name }}
                  </v-chip>
                  {{ `+${item.additionalLabels.length}` }}
                </div>
              </template>
              {{ getAdditionalLabels(item) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.label"
              :color="getChipColor(item.label, $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.label, $vuetify.theme.dark) }">
              {{ item.label.name }}
            </v-chip>
            <v-tooltip top v-else-if="item.additionalLabels.length > 1 && !item.label">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.additionalLabels[0], $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.additionalLabels[0], $vuetify.theme.dark) }">
                    {{ item.additionalLabels[0].name }}
                  </v-chip>
                  {{ `+${item.additionalLabels.length - 1}` }}
                </div>
              </template>
              {{ getAdditionalLabels(item, true) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.additionalLabels.length === 1"
              :color="getChipColor(item.additionalLabels[0], $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.additionalLabels[0], $vuetify.theme.dark) }">
              {{ item.additionalLabels[0].name }}
            </v-chip>
          </template>
          <template #[`field.stats.revenue`]="{ item }">
            {{
              item.stats && item.stats.revenue
                ? `$ ${parseFloat(item.stats.revenue).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : "-"
            }}
          </template>
          <template #[`field.createdAt`]="{ item }">
            {{ changeDateFormat(item.createdAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.expiresAt`]="{ item }">
            {{ item.expiresAt ? changeDateFormat(item.expiresAt, isUserUS($store.state.user.timezone)) : "-" }}
          </template>
          <template #[`field.privacyExpiresAt`]="{ item }">
            {{
              item.privacyExpiresAt ? changeDateFormat(item.privacyExpiresAt, isUserUS($store.state.user.timezone)) : "-"
            }}
          </template>
          <template #[`field.suspended`]="{ item }">
            {{ item.suspended ? $t("generic.yes") : $t("generic.no") }}
          </template>
          <template #[`field.suspensionsCount`]="{ item }">
            {{ item.suspensionsCount || "-" }}
          </template>
          <template #[`field.autoRenew`]="{ item }">
            <a
              :href="namecheapUrl(item.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="namecheap-link"
              @click.stop>
              {{ item.autoRenew ? $t("generic.on") : $t("generic.off") }}
            </a>
          </template>
          <template #[`field.robots`]="{ item }">
            {{ seoIndexingLabel(item.robots) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Settings',
                  icon: 'mdi-cog',
                  function: redirectAction.bind(this, {
                    name: 'domains_show',
                    params: { id: item.id },
                    query: { tab: 'domain_settings' },
                  }),
                },
                {
                  title: 'View Domain',
                  icon: 'mdi-eye',
                  function: redirectAction.bind(this, { name: 'domains_show', params: { id: item.id } }),
                },
              ]"></DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import definedAbility from "@/ability";
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import EmptyList from "@/components/custom/EmptyList";
  import DataListActions from "@/components/custom/DataListActions";
  import PlusIcon from "@/components/icons/PlusIcon";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import {
    DOMAIN_DEPLOYMENT_STATUS,
    DOMAIN_PARAMETER_QUERY_KEY,
    NAMECHEAP_DOMAIN_CONTROL_PANEL_URL,
    ROBOTS,
  } from "@/constant";
  import debounce from "lodash.debounce";
  import PaywallLimitModal from "@/components/custom/PaywallLimitModal.vue";
  // Mixins
  import paywallLimits from "@/mixins/paywallLimits";
  import DomainConnectModal from "@/components/custom/DomainConnectModal";
  import RowClick from "@/mixins/rowClickMixin";
  // Utility
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { getChipColor, getChipTextColor } from "@/assets/utilities/helpers/getChipColor";
  import CopyText from "@/components/custom/CopyText";
  export default {
    components: {
      PlusIcon,
      ResourceListWrapper,
      DataListActions,
      EmptyList,
      PaywallLimitModal,
      DomainConnectModal,
      CopyText,
    },
    props: {
      title: {
        type: String,
        required: true,
      },
      listPage: {
        type: Boolean,
        default: true,
      },
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
    },
    mixins: [paywallLimits, RowClick],
    data() {
      return {
        ability: definedAbility(),
        changeDateFormat,
        isUserUS,
        getChipColor,
        getChipTextColor,
        noData: false,
        loading: true,
        totalItems: 0,
        labelsList: [],
        filter: {},
        filters: [
          {
            // Q is a backend parameter that searches through
            // URL, IP, Name
            source: "q",
            alwaysOn: true,
          },
          {
            source: "label",
          },
        ],
        fields: [
          {
            source: "url",
            label: this.$t("resources.domains.fields.url"),
            sortable: true,
            width: "40%",
            full_width: "40%",
            id: "url",
          },
          {
            source: "name",
            label: this.$t("resources.domains.fields.name"),
            sortable: true,
            width: "40%",
            full_width: "40%",
            id: "name",
          },
          {
            source: "status",
            label: this.$t("resources.domains.fields.deployment_status"),
            sortable: true,
            width: "20%",
            full_width: "20%",
            id: "status",
          },
          {
            source: "alternativeContentParameter",
            label: this.$t("resources.domains.fields.parameter"),
            sortable: false,
            width: "20%",
            full_width: "20%",
            id: "alternativeContentParameter",
          },
          {
            source: "stats.revenue",
            label: this.$t("resources.domains.fields.revenue"),
            sortable: false,
            width: "20%",
            full_width: "20%",
            id: "revenue",
          },
          {
            source: "label.name",
            label: this.$t("resources.pages.fields.label"),
            sortable: true,
            width: "132px",
            full_width: "132px",
            cellClass: "label-cell",
            id: "label_name",
          },
          {
            source: "updatedAt",
            sortable: true,
            label: this.$t("resources.domains.fields.updated_at"),
            width: "134px",
            full_width: "134px",
            id: "updatedAt",
          },
          {
            source: "createdAt",
            sortable: true,
            label: this.$t("resources.domains.fields.created_at"),
            width: "134px",
            full_width: "134px",
            id: "createdAt",
          },
        ],
        displayPaywallLimitModal: false,
        connectDomainLoading: false,
        displayConnectDomainModal: false,
        DOMAIN_PARAMETER_QUERY_KEY,
        activeColumns: [],
      };
    },
    beforeMount() {
      if (this.ability.can("view", "homepage column")) {
        this.fields.splice(4, 0, {
          source: "generateHomepage",
          label: this.$t("resources.domains.fields.generate_homepage"),
          type: "boolean",
          sortable: true,
          width: "124px",
          full_width: "124px",
          id: "generateHomepage",
        });
      }

      if (this.ability.can("view", "domain suspension columns")) {
        // "Domain Expires At" must sit immediately after the "Created At" column.
        const createdAtIndex = this.fields.findIndex(field => field.id === "createdAt");
        this.fields.splice(
          createdAtIndex + 1,
          0,
          {
            source: "expiresAt",
            label: this.$t("resources.domains.fields.domain_expires_at"),
            sortable: true,
            width: "134px",
            full_width: "134px",
            defaultHidden: true,
            id: "expiresAt",
          },
          {
            source: "privacyExpiresAt",
            label: this.$t("resources.domains.fields.privacy_expires_at"),
            sortable: true,
            width: "134px",
            full_width: "134px",
            defaultHidden: true,
            id: "privacyExpiresAt",
          },
        );
        this.fields.push(
          {
            source: "suspended",
            label: this.$t("resources.domains.fields.suspended"),
            sortable: true,
            width: "124px",
            full_width: "124px",
            defaultHidden: true,
            id: "suspended",
          },
          {
            source: "suspensionsCount",
            label: this.$t("resources.domains.fields.suspensions"),
            sortable: false,
            width: "124px",
            full_width: "124px",
            defaultHidden: true,
            id: "suspensionsCount",
          },
          {
            source: "autoRenew",
            label: this.$t("resources.domains.fields.auto_renew"),
            sortable: true,
            width: "124px",
            full_width: "124px",
            defaultHidden: true,
            id: "autoRenew",
          },
          {
            source: "robots",
            label: this.$t("resources.domains.fields.seo_indexing"),
            sortable: true,
            width: "124px",
            full_width: "124px",
            defaultHidden: true,
            id: "robots",
          },
        );
      }
    },
    async mounted() {
      try {
        const { data } = await this.$admin.dataProvider.getList("labels", { disablePagination: true });
        this.labelsList = data;
      } catch (e) {
        this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
      }

      // Initialize active columns
      if (this.$store.state?.table?.domain?.activeColumns?.length > 0) {
        this.fields.forEach(field => {
          if (this.$store.state.table.domain.activeColumns.includes(field.id) || field.required) {
            this.$set(field, "active", true);
          }
        });
        this.updateColumns(this.fields, false);
      } else {
        // Initialize store if it doesn't exist
        if (!this.$store.state.table.domain) {
          this.$store.state.table.domain = { activeColumns: [] };
        }
        this.$store.state.table.domain.activeColumns = this.fields
          .filter(field => !field.defaultHidden)
          .map(field => field.id);
        this.fields = this.fields.map(field => {
          field.active = !field.defaultHidden;

          return field;
        });
        this.updateColumns(this.fields);
      }
    },
    methods: {
      getAdditionalLabels(item, removeFirst = false) {
        const labels = removeFirst ? item.additionalLabels.slice(1) : item.additionalLabels;

        return labels.map(label => label.name).join(", ");
      },
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      async connectDomainHandler() {
        if (this.ignorePaywallLimits) {
          if (this.ability.can("view", "original domain connect")) {
            this.$router.push({ name: "domains_create" });

            return;
          }

          this.displayConnectDomainModal = true;

          return;
        }

        this.connectDomainLoading = true;
        await this.updateLimitExceededData();
        if (this.limitsExceeded["domains"]) {
          this.displayPaywallLimitModal = true;
        } else {
          if (this.ability.can("view", "original domain connect")) {
            this.$router.push({ name: "domains_create" });
          } else {
            this.displayConnectDomainModal = true;
          }
        }
        this.connectDomainLoading = false;
      },
      update: debounce(function (source, value) {
        this.filter = {
          ...this.filter,
          [source]: value,
        };
      }, 400),
      getDomainStatusInfo(domain) {
        let deploymentStatusInfo = {};
        deploymentStatusInfo = DOMAIN_DEPLOYMENT_STATUS.find(
          deploymentStatus => deploymentStatus.status === domain.status,
        );

        if (!deploymentStatusInfo) return {};

        return deploymentStatusInfo;
      },
      redirectAction(resourceId) {
        this.$router.push(resourceId);
      },
      openDomainPurchase() {
        this.$router.push({ name: "domain_purchase" });
      },
      namecheapUrl(domainName) {
        return `${NAMECHEAP_DOMAIN_CONTROL_PANEL_URL}/${domainName}`;
      },
      seoIndexingLabel(robots) {
        if (robots === ROBOTS.allow) return this.$t("generic.on");
        if (robots === ROBOTS.disallow) return this.$t("generic.off");
        return this.$t("generic.custom");
      },
      updateColumns(columns, updateState = true) {
        this.activeColumns = columns.filter(field => !!field.active);
        if (updateState) {
          this.$store.commit(
            "SET_DOMAIN_TABLE_COLUMNS",
            this.activeColumns.map(field => field.id),
          );
        }
        if (this.activeColumns.length !== this.fields.length) {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", undefined);
          });
        } else {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", field.full_width);
          });
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  @import "../../@core/preset/preset/typography/index.scss";
  @import "../../@core/preset/preset/functions.scss";

  .default__badge {
    color: get-color("subtitle");
    @include text-caption();
    padding: 4px 12px;
    display: inline-block;
    border-radius: 4px;
    border: 1px solid get-color("border");
    margin-left: 8px;
  }
  .tooltip-content {
    width: 188px;
  }

  .namecheap-link {
    color: get-color("primary");
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .data-table-custom-class {
    ::v-deep {
      .v-data-table {
        td:not(:first-child):not(:last-child),
        th:not(:first-child):not(:last-child) {
          padding: 0px 4px;
        }

        th .v-input--checkbox {
          padding: 0 4px;
        }

        .v-data-table__checkbox {
          padding: 0 4px;
        }

        .data-list--action-btn {
          min-width: unset !important;
        }

        .item-actions {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
        }

        .v-btn--icon {
          width: unset !important;
        }
      }
    }
  }
</style>
